import { useState, useCallback } from 'react';
import { supabaseWithAuth } from '@/utils/supabaseWrapper';

interface EmailCheckResult {
  exists: boolean;
  isChecking: boolean;
  error: string | null;
}

/**
 * Hook pour vérifier si un email existe déjà dans la base de données
 */
export const useEmailCheck = () => {
  const [emailCheckResults, setEmailCheckResults] = useState<Record<string, EmailCheckResult>>({});

  /**
   * Vérifie si un email existe déjà
   */
  const checkEmailExists = useCallback(async (email: string): Promise<boolean> => {
    // Validation basique de l'email
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return false;
    }

    // Normaliser l'email
    const normalizedEmail = email.toLowerCase().trim();

    // Mettre à jour l'état pour indiquer qu'on vérifie
    setEmailCheckResults(prev => ({
      ...prev,
      [normalizedEmail]: {
        exists: false,
        isChecking: true,
        error: null
      }
    }));

    try {
      // Appeler la fonction RPC pour vérifier l'email avec protection auth
      const { data, error } = await supabaseWithAuth.rpc('check_email_exists', {
        email_to_check: normalizedEmail
      });

      if (error) {
        console.error('Erreur lors de la vérification de l\'email:', error);
        setEmailCheckResults(prev => ({
          ...prev,
          [normalizedEmail]: {
            exists: false,
            isChecking: false,
            error: 'Erreur lors de la vérification'
          }
        }));
        return false;
      }

      // Mettre à jour le résultat
      setEmailCheckResults(prev => ({
        ...prev,
        [normalizedEmail]: {
          exists: data || false,
          isChecking: false,
          error: null
        }
      }));

      return data || false;
    } catch (error) {
      console.error('Erreur inattendue lors de la vérification de l\'email:', error);
      setEmailCheckResults(prev => ({
        ...prev,
        [normalizedEmail]: {
          exists: false,
          isChecking: false,
          error: 'Erreur inattendue'
        }
      }));
      return false;
    }
  }, []);

  /**
   * Obtient le résultat de vérification pour un email donné
   */
  const getEmailCheckResult = useCallback((email: string): EmailCheckResult => {
    const normalizedEmail = email.toLowerCase().trim();
    return emailCheckResults[normalizedEmail] || {
      exists: false,
      isChecking: false,
      error: null
    };
  }, [emailCheckResults]);

  /**
   * Réinitialise les résultats de vérification
   */
  const clearEmailCheckResults = useCallback(() => {
    setEmailCheckResults({});
  }, []);

  return {
    checkEmailExists,
    getEmailCheckResult,
    clearEmailCheckResults
  };
};